"use client";

import type { RefObject } from "react";
import {
  CopyButton,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import type { ROC_CURVE_CALCULATOR_STRINGS } from "@/components/tools/roc-curve-calculator";
import {
  CHART_BLUE,
  CHART_INK,
  CHART_MUTED,
  CHART_RED,
  Chart,
  ChartDownloads,
} from "@/components/tools/stats-clinical/chart";
import {
  DataGrid,
  WorkingLine,
} from "@/components/tools/stats-clinical/data-grid";
import { t } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import {
  type AucBand,
  aucBand,
  countsAt,
  type RocAnalysis,
  type RocPoint,
} from "@/lib/tool-kit/stats-clinical/roc";
import { diagnosticMeasures } from "@/lib/tool-kit/stats-research/diagnostic";
import {
  apaBounded,
  type Formatter,
  fixed,
  formatP,
} from "@/lib/tool-kit/stats-research/format";
import type { Interval } from "@/lib/tool-kit/stats-research/proportions";

/*
 * Plot and result panels of the ROC curve calculator. Strings are the widget's own.
 */

type Strings = typeof ROC_CURVE_CALCULATOR_STRINGS;

export type Criterion = "youden" | "topLeft";

const DASH = "—";
const MAX_TABLE_ROWS = 60;

const BAND_KEY: Record<AucBand, keyof Strings> = {
  none: "bandNone",
  poor: "bandPoor",
  acceptable: "bandAcceptable",
  excellent: "bandExcellent",
  outstanding: "bandOutstanding",
};

function three(fmt: Formatter, value: number): string {
  return Number.isFinite(value) ? fixed(fmt, value, 3) : DASH;
}

function percent(fmt: Formatter, value: number): string {
  return `${fmt(value * 100, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}

function ruleText(
  strings: Strings,
  fmt: Formatter,
  threshold: number,
  reversed: boolean
): string {
  const value = fmt(reversed ? -threshold : threshold, {
    maximumFractionDigits: 6,
  });
  return t(reversed ? strings.ruleAtMost : strings.ruleAtLeast, { value });
}

function intervalText(
  fmt: Formatter,
  interval: Interval | null | undefined,
  digits = 1
): string {
  if (!interval) {
    return DASH;
  }
  const show = (v: number) =>
    fmt(v * 100, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    });
  return `${show(interval.lower)}% – ${show(interval.upper)}%`;
}

function ratioText(
  fmt: Formatter,
  ratio: { value: number; interval: Interval | null } | null
): [string, string] {
  if (!ratio) {
    return [DASH, DASH];
  }
  const show = (v: number) => fixed(fmt, v, 2);
  return [
    show(ratio.value),
    ratio.interval
      ? `${show(ratio.interval.lower)} – ${show(ratio.interval.upper)}`
      : DASH,
  ];
}

function RocPlot({
  analysis,
  marker,
  strings,
  fmt,
  svgRef,
}: {
  analysis: RocAnalysis;
  marker: RocPoint;
  strings: Strings;
  fmt: Formatter;
  svgRef: RefObject<SVGSVGElement | null>;
}) {
  return (
    <Chart
      formatX={(v) => fmt(v, { maximumFractionDigits: 1 })}
      formatY={(v) => fmt(v, { maximumFractionDigits: 1 })}
      height={470}
      idPrefix="roc"
      label={t(strings.plotLabel, { auc: three(fmt, analysis.auc) })}
      svgRef={svgRef}
      width={560}
      xDomain={[0, 1]}
      xLabel={strings.xAxis}
      yDomain={[0, 1]}
      yLabel={strings.yAxis}
    >
      {(area) => {
        const path = analysis.points
          .map(
            (p, i) =>
              `${i === 0 ? "M" : "L"}${area.x(1 - p.specificity).toFixed(2)} ${area.y(p.sensitivity).toFixed(2)}`
          )
          .join(" ");
        const cx = area.x(1 - marker.specificity);
        const cy = area.y(marker.sensitivity);
        const labelRight = cx < (area.left + area.right) / 2;
        return (
          <>
            <line
              stroke={CHART_MUTED}
              strokeDasharray="5 4"
              x1={area.x(0)}
              x2={area.x(1)}
              y1={area.y(0)}
              y2={area.y(1)}
            />
            <path
              d={path}
              fill="none"
              stroke={CHART_BLUE}
              strokeLinejoin="round"
              strokeWidth={2.5}
            />
            <circle
              cx={cx}
              cy={cy}
              fill={CHART_RED}
              r={5.5}
              stroke="#ffffff"
              strokeWidth={1.5}
            />
            <text
              fill={CHART_RED}
              fontFamily="system-ui, sans-serif"
              fontSize={11}
              textAnchor={labelRight ? "start" : "end"}
              x={cx + (labelRight ? 10 : -10)}
              y={cy + 18}
            >
              {ruleText(strings, fmt, marker.threshold, analysis.reversed)}
            </text>
            <text
              fill={CHART_INK}
              fontFamily="system-ui, sans-serif"
              fontSize={13}
              fontWeight={600}
              textAnchor="end"
              x={area.right - 12}
              y={area.bottom - 14}
            >
              {t(strings.aucLabel, { auc: three(fmt, analysis.auc) })}
            </text>
          </>
        );
      }}
    </Chart>
  );
}

function CutoffTable({
  tp,
  fp,
  fn,
  tn,
  strings,
  fmt,
}: {
  tp: number;
  fp: number;
  fn: number;
  tn: number;
  strings: Strings;
  fmt: Formatter;
}) {
  const d = diagnosticMeasures(tp, fp, fn, tn);
  const [lrp, lrpCi] = ratioText(fmt, d.lrPositive);
  const [lrn, lrnCi] = ratioText(fmt, d.lrNegative);
  const proportion = (label: string, entry: typeof d.sensitivity) => [
    label,
    entry ? percent(fmt, entry.value) : DASH,
    intervalText(fmt, entry),
  ];
  return (
    <DataGrid
      head={[
        strings.columnMeasure,
        strings.columnEstimate,
        strings.columnInterval,
      ]}
      rows={[
        proportion(strings.sensitivity, d.sensitivity),
        proportion(strings.specificity, d.specificity),
        proportion(strings.ppv, d.ppv),
        proportion(strings.npv, d.npv),
        [strings.lrPositive, lrp, lrpCi],
        [strings.lrNegative, lrn, lrnCi],
        [strings.youdenJ, three(fmt, d.youden), ""],
      ]}
    />
  );
}

function ThresholdTable({
  analysis,
  strings,
  fmt,
}: {
  analysis: RocAnalysis;
  strings: Strings;
  fmt: Formatter;
}) {
  const rows = analysis.points.slice(1);
  const shown = rows.slice(0, MAX_TABLE_ROWS);
  const csv = () => {
    const head = [
      "threshold",
      "tp",
      "fp",
      "fn",
      "tn",
      "sensitivity",
      "specificity",
      "youden_j",
    ];
    const lines = rows.map((p) =>
      [
        analysis.reversed ? -p.threshold : p.threshold,
        p.tp,
        p.fp,
        p.fn,
        p.tn,
        p.sensitivity,
        p.specificity,
        p.youden,
      ].join(",")
    );
    downloadText(
      "roc-points.csv",
      [head.join(","), ...lines].join("\n"),
      "text/csv;charset=utf-8"
    );
  };
  return (
    <section className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.pointsHeading}</h3>
      <p className="text-muted-foreground text-sm">
        {analysis.reversed
          ? strings.pointsNoteAtMost
          : strings.pointsNoteAtLeast}
      </p>
      <div className="max-h-80 overflow-auto rounded-lg">
        <DataGrid
          head={[
            strings.columnCutoff,
            strings.columnSens,
            strings.columnSpec,
            strings.columnJ,
            "TP",
            "FP",
            "FN",
            "TN",
          ]}
          rows={shown.map((p) => [
            fmt(analysis.reversed ? -p.threshold : p.threshold, {
              maximumFractionDigits: 6,
            }),
            three(fmt, p.sensitivity),
            three(fmt, p.specificity),
            three(fmt, p.youden),
            fmt(p.tp),
            fmt(p.fp),
            fmt(p.fn),
            fmt(p.tn),
          ])}
        />
      </div>
      {rows.length > shown.length ? (
        <p className="text-muted-foreground text-xs">
          {t(strings.pointsMore, {
            shown: fmt(shown.length),
            total: fmt(rows.length),
          })}
        </p>
      ) : null}
      <button className={BUTTON_SECONDARY} onClick={csv} type="button">
        {strings.downloadCsv}
      </button>
    </section>
  );
}

export function RocSummary({
  analysis,
  positives,
  negatives,
  criterion,
  customCutoff,
  strings,
  fmt,
  svgRef,
}: {
  analysis: RocAnalysis;
  positives: readonly number[];
  negatives: readonly number[];
  criterion: Criterion;
  customCutoff: number | null;
  strings: Strings;
  fmt: Formatter;
  svgRef: RefObject<SVGSVGElement | null>;
}) {
  const optimum = criterion === "youden" ? analysis.youden : analysis.topLeft;
  const { auc, delong, hanleyMcNeil } = analysis;
  const rule = ruleText(strings, fmt, optimum.threshold, analysis.reversed);
  const custom =
    customCutoff === null
      ? null
      : countsAt(positives, negatives, customCutoff, !analysis.reversed);
  const report = t(strings.report, {
    auc: three(fmt, auc),
    lower: three(fmt, delong.lower),
    upper: three(fmt, delong.upper),
    p:
      analysis.p < 0.001
        ? `< ${apaBounded(fmt, 0.001, 3)}`
        : `= ${apaBounded(fmt, analysis.p, 3)}`,
    rule,
    sens: percent(fmt, optimum.sensitivity),
    spec: percent(fmt, optimum.specificity),
  });
  return (
    <div aria-live="polite" className="space-y-6">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <RocPlot
        analysis={analysis}
        fmt={fmt}
        marker={optimum}
        strings={strings}
        svgRef={svgRef}
      />
      <ChartDownloads
        baseName="roc-curve"
        pngLabel={strings.downloadPng}
        svgLabel={strings.downloadSvg}
        svgRef={svgRef}
      />
      <StatGrid>
        <StatCard emphasis label={strings.aucCard} value={three(fmt, auc)} />
        <StatCard
          label={strings.ciCard}
          value={`${three(fmt, delong.lower)} – ${three(fmt, delong.upper)}`}
        />
        <StatCard label={strings.pCard} value={formatP(fmt, analysis.p)} />
      </StatGrid>
      <p className="text-foreground text-sm leading-relaxed">
        {t(strings.interpretation, {
          auc: three(fmt, auc),
          band: strings[BAND_KEY[aucBand(auc)]],
        })}{" "}
        <span className="text-muted-foreground">
          {strings.interpretationNote}
        </span>
      </p>
      {auc < 0.5 ? (
        <p className="text-foreground text-sm">{strings.belowChance}</p>
      ) : null}
      <DataGrid
        head={[strings.columnMethod, strings.columnSe, strings.columnInterval]}
        rows={[
          [
            strings.methodDelong,
            three(fmt, delong.se),
            `${three(fmt, delong.lower)} – ${three(fmt, delong.upper)}`,
          ],
          [
            strings.methodHanley,
            three(fmt, hanleyMcNeil.se),
            `${three(fmt, hanleyMcNeil.lower)} – ${three(fmt, hanleyMcNeil.upper)}`,
          ],
        ]}
      />
      <section className="space-y-2">
        <h3 className={WIDGET_H3}>
          {t(strings.optimumHeading, {
            criterion:
              criterion === "youden"
                ? strings.criterionYouden
                : strings.criterionTopLeft,
          })}
        </h3>
        <p className="text-foreground text-sm">
          {t(strings.optimumText, {
            rule,
            sens: percent(fmt, optimum.sensitivity),
            spec: percent(fmt, optimum.specificity),
            j: three(fmt, optimum.youden),
          })}
        </p>
        <CutoffTable
          fmt={fmt}
          fn={optimum.fn}
          fp={optimum.fp}
          strings={strings}
          tn={optimum.tn}
          tp={optimum.tp}
        />
        <p className="text-muted-foreground text-xs">{strings.optimumNote}</p>
      </section>
      {custom ? (
        <section className="space-y-2">
          <h3 className={WIDGET_H3}>
            {t(strings.customHeading, {
              rule: t(
                analysis.reversed ? strings.ruleAtMost : strings.ruleAtLeast,
                {
                  value: fmt(customCutoff ?? 0, { maximumFractionDigits: 6 }),
                }
              ),
            })}
          </h3>
          <CutoffTable
            fmt={fmt}
            fn={custom.fn}
            fp={custom.fp}
            strings={strings}
            tn={custom.tn}
            tp={custom.tp}
          />
        </section>
      ) : null}
      <div className="flex flex-wrap items-center gap-3">
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyResult}
          text={report}
        />
        <span className="font-mono text-muted-foreground text-xs">
          {report}
        </span>
      </div>
      <ThresholdTable analysis={analysis} fmt={fmt} strings={strings} />
      <div className={`${CARD} space-y-2`}>
        <h3 className={WIDGET_H3}>{strings.howCalculated}</h3>
        <WorkingLine>
          {t(strings.workingAuc, {
            u: fmt(analysis.u, { maximumFractionDigits: 1 }),
            m: fmt(analysis.positives),
            n: fmt(analysis.negatives),
            auc: fixed(fmt, auc, 4),
          })}
        </WorkingLine>
        <WorkingLine>
          {t(strings.workingSe, {
            se: fixed(fmt, delong.se, 4),
            lower: three(fmt, delong.lower),
            upper: three(fmt, delong.upper),
          })}
        </WorkingLine>
        <p className="text-muted-foreground text-sm">{strings.workingNote}</p>
      </div>
    </div>
  );
}
